import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  User,
  MessageSquare,
  CheckCircle
} from 'lucide-react'

import Section from '../components/ui/Section'
import GlassCard from '../components/ui/GlassCard'
import Input from '../components/ui/Input'
import Textarea from '../components/ui/Textarea'
import Button from '../components/ui/Button'
import { useStore } from '../store/useStore'

const Contact = () => {
  const { t } = useTranslation()
  const { addMessage } = useStore()
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    try {
      // Add message to store (this will create notification for admins)
      await addMessage({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      })
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      })
      
      setSubmitted(true)
      
      // Reset submitted state after 3 seconds
      setTimeout(() => {
        setSubmitted(false)
      }, 3000)
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error)
      alert('Une erreur est survenue lors de l\'envoi du message. Veuillez réessayer.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const contactInfo = [
    {
      icon: MapPin,
      title: t('contact.info.address'),
      content: '444 Avenue Yvon-Berger\nLaval, Québec H7L 2C2, Canada',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Phone,
      title: t('contact.info.phone'),
      content: '(450) 628-2696',
      link: 'tel:+14506282696',
      color: 'from-green-500 to-emerald-500',
    },
    {
      icon: Mail,
      title: t('contact.info.email'),
      content: 'admin@residencemassena.com',
      link: 'mailto:admin@residencemassena.com',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: Clock,
      title: t('contact.info.hours'),
      content: `Lun - Ven : 8h - 17h\nSamedi : 9h - 15h\nDimanche : Fermé`,
      color: 'from-orange-500 to-red-500',
    },
  ]

  return (
    <div className="min-h-screen pt-32 pb-20">
      <Section
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <GlassCard className="p-8">
              <h3 className="text-2xl font-bold gradient-text mb-6">
                Envoyez-nous un message
              </h3>

              {submitted && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3"
                >
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <p className="text-green-800 text-sm">
                    Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
                  </p>
                </motion.div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  label={t('contact.form.name')}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  icon={User}
                  placeholder="John Doe"
                  required
                />

                <Input
                  label={t('contact.form.email')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  icon={Mail}
                  placeholder="john@example.com"
                  required
                />

                <Input
                  label={t('contact.form.phone')}
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  icon={Phone}
                  placeholder="+33 1 23 45 67 89"
                />

                <Input
                  label={t('contact.form.subject')}
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  icon={MessageSquare}
                  placeholder="Demande d'information"
                  required
                />

                <Textarea
                  label={t('contact.form.message')}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  placeholder="Votre message..."
                  required
                />

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  icon={Send}
                  loading={loading}
                >
                  {loading ? t('contact.form.sending') : t('contact.form.send')}
                </Button>
              </form>
            </GlassCard>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <GlassCard className="p-8">
              <h3 className="text-2xl font-bold gradient-text mb-6">
                {t('contact.info.title')}
              </h3>
              
              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="flex items-start space-x-4 group"
                    >
                      <div className="relative">
                        <div className={`absolute inset-0 bg-gradient-to-br ${info.color} blur-lg opacity-50 group-hover:opacity-70 transition-opacity`} />
                        <div className={`relative glass-card p-3 bg-gradient-to-br ${info.color}`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-black mb-1">
                          {info.title}
                        </h4>
                        {info.link ? (
                          <a
                            href={info.link}
                            className="text-gray-700 hover:text-primary-600 transition-colors whitespace-pre-line"
                          >
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-gray-700 whitespace-pre-line">
                            {info.content}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </GlassCard>

            {/* Map */}
            <GlassCard className="p-2 overflow-hidden">
              <div className="w-full h-80 rounded-xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2795.123456789!2d-73.7123456!3d45.6123456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc91a1234567890%3A0x1234567890abcdef!2s444%20Avenue%20Yvon-Berger%2C%20Laval%2C%20QC%20H7L%202C2%2C%20Canada!5e0!3m2!1sen!2sca!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Résidences Masséna - Laval"
                />
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </Section>
    </div>
  )
}

export default Contact

